import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/TwitterAnalyticsPostsByDate.graphql';
import { Query, QuerytwitterAnalyticsPostsByDateArgs } from '../../__generated__/globalTypes';

export const useTwitterAnalyticsPostsByDateQuery = (
  options?: QueryHookOptions<Query, QuerytwitterAnalyticsPostsByDateArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerytwitterAnalyticsPostsByDateArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useTwitterAnalyticsPostsByDateLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerytwitterAnalyticsPostsByDateArgs>
): QueryResult<Query, QuerytwitterAnalyticsPostsByDateArgs> & {
  getTwitterAnalyticsPostsByDate: LazyQueryExecFunction<Query, QuerytwitterAnalyticsPostsByDateArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getTwitterAnalyticsPostsByDate, result] = useLazyQuery<Query, QuerytwitterAnalyticsPostsByDateArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getTwitterAnalyticsPostsByDate, ...result };
};

export const useTwitterAnalyticsPostsByDatePromiseQuery = () => {
  const client = useApolloClient();

  const getTwitterAnalyticsPostsByDate = useCallback(
    (options?: Omit<QueryOptions<QuerytwitterAnalyticsPostsByDateArgs, Query>, 'query'>) =>
      client.query<Query, QuerytwitterAnalyticsPostsByDateArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getTwitterAnalyticsPostsByDate };
};
// Suspense Query Hooks
export const useTwitterAnalyticsPostsByDateSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerytwitterAnalyticsPostsByDateArgs>
) => {
  const result = useSuspenseQuery<Query, QuerytwitterAnalyticsPostsByDateArgs>(QUERY, {
    ...options
  });

  return result;
};
